import React, { useState, useEffect, useRef } from 'react';
import { API_URL } from "../config";
import { useSearchParams } from "react-router-dom";

const Realisations = () => {
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [videos, setVideos] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const modalRef = useRef(null);
    const [searchParams] = useSearchParams();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const catSlug = searchParams.get('cat');
        if (catSlug && categories.length > 0) {
            const index = categories.findIndex(c => c.slug === catSlug);
            if (index !== -1) {
                setActiveTab(index + 1);
            }
        }
    }, [categories, searchParams]);

    useEffect(() => {
        fetch(`${API_URL}/categories?type=video`)
            .then((res) => res.json())
            .then((data) => {
                setCategories(data.categories || []);
            });
    }, []);

    useEffect(() => {
        let url;
        if (activeTab === 0) {
            url = `${API_URL}/videos`;
        } else {
            const slug = categories[activeTab - 1]?.slug;
            url = `${API_URL}/videos/category/${slug}`;
        }

        setIsLoading(true);

        setTimeout(() => {
            fetch(url)
                .then((res) => res.json())
                .then((data) => {
                    setVideos(data.videos || []);
                    setTimeout(() => setIsLoading(false), 50);
                });
        }, 300);
    }, [activeTab, categories]);

    function getEmbedUrl(url) {
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) return 'https://player.vimeo.com/video/' + vimeoMatch[1] + '?autoplay=1';
        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
        if (youtubeMatch) return 'https://www.youtube.com/embed/' + youtubeMatch[1] + '?autoplay=1';
        return url;
    }

    const handleClick = (index) => {
        if (index === activeTab) return;
        setActiveTab(index);
    };

    const handleProjectClick = (video) => {
        setSelectedVideo(video);
        setShowModal(true);
        setIsClosing(false);
        document.body.classList.add('lock');
    };

    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setShowModal(false);
            setSelectedVideo(null);
            setIsClosing(false);
            document.body.classList.remove('lock');
        }, 400);
    };

    const handleCloseModal = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            closeModal();
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && showModal) closeModal();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showModal]);

    function formatDate(video) {
        if (!video.post_date) return '';
        return new Date(video.post_date * 1000).toLocaleDateString('fr-FR', {
            day: '2-digit', month: 'long', year: 'numeric',
        });
    }

    return (
        <div className="min-[320px]:py-0 xl:py-32 reals">
            <h1 className="min-[320px]:px-4 xl:px-0 reals-title min-[320px]:text-left xl:text-center min-[320px]:my-4 xl:mt-12 min-[320px]:pb-4 font-bold min-[320px]:text-36 xl:text-title-49">
                Nos Réalisations
            </h1>

            <div className="reals-toolbar">
                <div className="tabs-container">
                    <div
                        onClick={() => handleClick(0)}
                        className={`tab-item ${activeTab === 0 ? 'active' : ''}`}
                    >
                        Voir tout
                    </div>
                    {categories.map((cat, index) => (
                        <div
                            key={cat.id}
                            onClick={() => handleClick(index + 1)}
                            className={`tab-item ${activeTab === index + 1 ? 'active' : ''}`}
                        >
                            {cat.name}
                        </div>
                    ))}
                </div>

                <div className="view-toggle">
                    <button
                        className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => setViewMode('grid')}
                        title="Grille"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="1" y="1" width="6" height="6" rx="1" />
                            <rect x="9" y="1" width="6" height="6" rx="1" />
                            <rect x="1" y="9" width="6" height="6" rx="1" />
                            <rect x="9" y="9" width="6" height="6" rx="1" />
                        </svg>
                    </button>
                    <button
                        className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => setViewMode('list')}
                        title="Liste"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <line x1="1" y1="3" x2="15" y2="3" />
                            <line x1="1" y1="8" x2="15" y2="8" />
                            <line x1="1" y1="13" x2="15" y2="13" />
                        </svg>
                    </button>
                </div>
            </div>

            <p className="reals-counter">
                {videos.length} réalisation{videos.length > 1 ? 's' : ''}
            </p>

            <div className={`reals-view reals-content-wrapper ${isLoading ? 'loading' : ''}`}>
                {viewMode === 'grid' ? (
                    <div className="reals-grid">
                        {videos.map((video, index) => (
                            <div
                                key={video.id}
                                className="reals-grid-item"
                                style={{ animationDelay: `${index * 0.06}s` }}
                                onClick={() => handleProjectClick(video)}
                            >
                                {video.thumbnail ? (
                                    <img src={`${video.thumbnail}`} alt={video.title} />
                                ) : (
                                    <div className="card-no-thumb">Pas d'aperçu</div>
                                )}
                                <div className="reals-grid-overlay">
                                    <h2>{video.title}</h2>
                                    {video.category_name && (
                                        <div className="reals-grid-category">{video.category_name}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="reals-list">
                        <div className="reals-list-header">
                            <span className="reals-list-col-thumb"></span>
                            <span className="reals-list-col-title">Titre</span>
                            <span className="reals-list-col-cat">Catégorie</span>
                            <span className="reals-list-col-action"></span>
                        </div>
                        {videos.map((video, index) => (
                            <div
                                key={video.id}
                                className="reals-list-item"
                                style={{ animationDelay: `${index * 0.04}s` }}
                                onClick={() => handleProjectClick(video)}
                            >
                                <div className="reals-list-col-thumb">
                                    {video.thumbnail ? (
                                        <img src={`${video.thumbnail}`} alt={video.title} />
                                    ) : (
                                        <div className="reals-list-no-thumb"></div>
                                    )}
                                </div>
                                <div className="reals-list-col-title">
                                    <h2>{video.title}</h2>
                                    {video.description && (
                                        <p>{video.description.substring(0, 80)}{video.description.length > 80 ? '...' : ''}</p>
                                    )}
                                </div>
                                <div className="reals-list-col-cat">
                                    {video.category_name && (
                                        <span className="reals-list-badge">{video.category_name}</span>
                                    )}
                                </div>
                                <div className="reals-list-col-date">
                                    {video.created_at && formatDate(video.created_at)}
                                </div>
                                <div className="reals-list-col-action">
                                    <span className="reals-list-play">▶</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModal && selectedVideo && (
                <div
                    className={`reals-modal-overlay ${isClosing ? 'closing' : ''}`}
                    onClick={handleCloseModal}
                >
                    <div className="reals-modal-box" ref={modalRef}>
                        <div className="reals-modal-iframe-wrapper">
                            <iframe
                                src={getEmbedUrl(selectedVideo.url)}
                                frameBorder="0"
                                allow="autoplay; fullscreen"
                                allowFullScreen
                            />
                        </div>
                        <div className="reals-modal-bottom">
                            <div className="reals-modal-info">
                                <h3>{selectedVideo.title}</h3>
                                {selectedVideo.description && (
                                    <p className="modal-description">{selectedVideo.description}</p>
                                )}
                                {selectedVideo.category_name && (
                                    <span className="modal-category">{selectedVideo.category_name}</span>
                                )}
                            </div>
                            <button onClick={closeModal} className="reals-modal-close" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Realisations;